import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPickupLocationsRequest,
  fetchPickupLocationsSuccess,
  fetchPickupLocationsFailure,
  createPickupLocationRequest,
  createPickupLocationSuccess,
  createPickupLocationFailure,
  updatePickupLocationRequest,
  updatePickupLocationSuccess,
  updatePickupLocationFailure,
  deletePickupLocationRequest,
  deletePickupLocationSuccess,
  deletePickupLocationFailure,
  setDefaultPickupLocationRequest,
  setDefaultPickupLocationSuccess,
  setDefaultPickupLocationFailure,
} from "../slices/pickupLocation.slice";
import {
  getPickupLocations,
  createPickupLocation,
  updatePickupLocation,
  deletePickupLocation,
} from "@/services/pickupLocationService";
import {
  CreatePickupLocationDto,
  UpdatePickupLocationDto,
  FetchPickupLocationsOptions,
} from "@/types/models/pickupLocation";
import { toApiError } from "@/lib/errorHelpers";

function* fetchPickupLocationsSaga(
  action: PayloadAction<FetchPickupLocationsOptions>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(getPickupLocations, action.payload);
    yield put(
      fetchPickupLocationsSuccess({
        data: response.data || [],
        pagination: response.pagination || {
          page: action.payload.page || 1,
          total: 0,
          pageSize: action.payload.page_size || 10,
          totalPages: 1,
        },
      }),
    );
  } catch (error: any) {
    yield put(fetchPickupLocationsFailure(toApiError(error)));
  }
}

function* createPickupLocationSaga(
  action: PayloadAction<CreatePickupLocationDto>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(createPickupLocation, action.payload);
    const data = response?.data || response;
    yield put(createPickupLocationSuccess(data));
  } catch (error: any) {
    yield put(createPickupLocationFailure(toApiError(error)));
  }
}

function* updatePickupLocationSaga(
  action: PayloadAction<{ id: string; data: UpdatePickupLocationDto }>,
): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
    const response: any = yield call(updatePickupLocation, id, data);
    const payload = response?.data || response;
    yield put(updatePickupLocationSuccess(payload));
  } catch (error: any) {
    yield put(updatePickupLocationFailure(toApiError(error)));
  }
}

function* deletePickupLocationSaga(
  action: PayloadAction<{ id: string }>,
): Generator<any, void, any> {
  try {
    yield call(deletePickupLocation, action.payload.id);
    yield put(deletePickupLocationSuccess({ id: action.payload.id }));
  } catch (error: any) {
    yield put(deletePickupLocationFailure(toApiError(error)));
  }
}

function* setDefaultPickupLocationSaga(
  action: PayloadAction<{ id: string }>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(updatePickupLocation, action.payload.id, {
      is_default: true,
    });
    const payload = response?.data || response;
    yield put(setDefaultPickupLocationSuccess(payload));
  } catch (error: any) {
    yield put(setDefaultPickupLocationFailure(toApiError(error)));
  }
}

export function* pickupLocationSaga(): Generator<any, void, any> {
  yield takeLatest(fetchPickupLocationsRequest.type, fetchPickupLocationsSaga);
  yield takeLatest(createPickupLocationRequest.type, createPickupLocationSaga);
  yield takeLatest(updatePickupLocationRequest.type, updatePickupLocationSaga);
  yield takeLatest(deletePickupLocationRequest.type, deletePickupLocationSaga);
  yield takeLatest(
    setDefaultPickupLocationRequest.type,
    setDefaultPickupLocationSaga,
  );
}

export default pickupLocationSaga;
